import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface ImpersonationState {
  adminUserId: number;
  adminUserName: string;
  targetUserId: number;
  targetUserName: string;
  startedAt: string;
}

const IMPERSONATION_KEY = 'impersonation_state';

/**
 * Service to manage user impersonation state.
 *
 * When a Super Admin impersonates another user:
 * 1. The original admin info is stored in localStorage
 * 2. The isImpersonating$ observable emits true
 * 3. A banner is shown to indicate impersonation mode
 *
 * This allows the admin to exit impersonation and return to their session.
 */
@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private impersonationStateSubject = new BehaviorSubject<ImpersonationState | null>(null);

  impersonationState$ = this.impersonationStateSubject.asObservable();

  constructor() {
    this.loadStateFromStorage();
  }

  private loadStateFromStorage(): void {
    try {
      const stored = localStorage.getItem(IMPERSONATION_KEY);
      if (stored) {
        const state = JSON.parse(stored) as ImpersonationState;
        this.impersonationStateSubject.next(state);
      }
    } catch {
      localStorage.removeItem(IMPERSONATION_KEY);
    }
  }

  startImpersonation(
    adminUserId: number,
    adminUserName: string,
    targetUserId: number,
    targetUserName: string
  ): void {
    const state: ImpersonationState = {
      adminUserId,
      adminUserName,
      targetUserId,
      targetUserName,
      startedAt: new Date().toISOString()
    };

    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    this.impersonationStateSubject.next(state);
  }

  endImpersonation(): void {
    localStorage.removeItem(IMPERSONATION_KEY);
    this.impersonationStateSubject.next(null);
  }

  isImpersonating(): boolean {
    return this.impersonationStateSubject.value !== null;
  }

  getState(): ImpersonationState | null {
    return this.impersonationStateSubject.value;
  }

  getAdminUserId(): number | null {
    return this.impersonationStateSubject.value?.adminUserId ?? null;
  }

  getTargetUserId(): number | null {
    return this.impersonationStateSubject.value?.targetUserId ?? null;
  }

  /**
   * Updates the target user's display name in the impersonation state.
   * Called after a successful profile save so the topbar reflects the new name
   * without requiring an exit and re-login of impersonation.
   */
  updateTargetUserName(newName: string): void {
    const currentState = this.impersonationStateSubject.value;
    if (!currentState) {
      return;
    }

    const updatedState: ImpersonationState = { ...currentState, targetUserName: newName };
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(updatedState));
    this.impersonationStateSubject.next(updatedState);
  }
}
